import React, { useState } from 'react'
import { useQuery, useMutation, useLazyQuery, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress, Select,
  MenuItem, FormControl, InputLabel, Drawer, Divider, Stack,
  IconButton, Tooltip, Modal,
} from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import SendIcon from '@mui/icons-material/Send'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn'
import RefreshIcon from '@mui/icons-material/Refresh'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import DataTable from 'react-data-table-component'
import OrderComponent from '../components/Order/Order'
import { getOrderById as GET_ORDER_BY_ID_QUERY } from '../apollo/queries'

// ─── GQL ─────────────────────────────────────────────────────────────────────

const GET_ORDER_BY_ID = gql`${GET_ORDER_BY_ID_QUERY}`

const ALL_TICKETS = gql`
  query AllSupportTickets($queue: TicketQueue, $status: TicketStatus, $page: Int, $limit: Int) {
    allSupportTickets(queue: $queue, status: $status, page: $page, limit: $limit) {
      id ticketNumber orderId orderDisplayId userId restaurantId
      requesterType riderId
      issueType assignedQueue description evidenceUrls
      status resolutionType resolutionNote resolvedAt
      createdAt updatedAt
      customer { id name email phone }
      rider { id name phone }
      refundRequest {
        id requestedAmount approvedAmount status affectedItems
        restaurantDecision adminDecision faultParty
        restaurantDecisionNote adminDecisionNote
        customerChoice choiceMadeBy creditScope creditRestaurantName
        bonusAmount creditTotal walletRefundAmount cardRefundAmount cardLabel
        cardRefundStatus cardRefundError
      }
      messages { id senderType senderId senderName message evidenceUrls affectedItems issueType isInternal isRead createdAt }
    }
  }
`

const ADMIN_MESSAGE = gql`
  mutation AdminMessageTicket($ticketId: ID!, $message: String!, $isInternal: Boolean) {
    adminMessageTicket(ticketId: $ticketId, message: $message, isInternal: $isInternal) {
      id senderType senderName message isInternal isRead createdAt
    }
  }
`

const ADMIN_REVIEW = gql`
  mutation AdminReviewRefund($ticketId: ID!, $decision: String!, $approvedAmount: Float, $note: String, $faultParty: String) {
    adminReviewRefund(ticketId: $ticketId, decision: $decision, approvedAmount: $approvedAmount, note: $note, faultParty: $faultParty) {
      id status approvedAmount adminDecision customerChoice
    }
  }
`

const ADMIN_RECORD_CHOICE = gql`
  mutation AdminRecordRefundChoice($ticketId: ID!, $choice: String!) {
    adminRecordRefundChoice(ticketId: $ticketId, choice: $choice) {
      id status customerChoice cardRefundStatus
    }
  }
`

const ADMIN_RETRY_CARD = gql`
  mutation AdminRetryCardRefund($ticketId: ID!) {
    adminRetryCardRefund(ticketId: $ticketId) {
      id status cardRefundStatus cardRefundError
    }
  }
`

const REFUND_BONUS = gql`
  query RefundBonusSettings {
    refundBonusSettings {
      platform { type value }
      store { type value }
    }
  }
`

const SAVE_REFUND_BONUS = gql`
  mutation SaveRefundBonusSettings($platformType: String!, $platformValue: Float!, $storeType: String!, $storeValue: Float!) {
    saveRefundBonusSettings(platformType: $platformType, platformValue: $platformValue, storeType: $storeType, storeValue: $storeValue) {
      platform { type value }
      store { type value }
    }
  }
`

const ADMIN_OVERRIDE = gql`
  mutation AdminOverrideCredit($ticketId: ID!, $amount: Float!, $note: String) {
    adminOverrideCredit(ticketId: $ticketId, amount: $amount, note: $note) {
      id amount balanceAfter
    }
  }
`

const ADMIN_CLOSE = gql`
  mutation AdminCloseTicket($ticketId: ID!, $resolution: String!, $note: String) {
    adminCloseTicket(ticketId: $ticketId, resolution: $resolution, note: $note) {
      id status resolvedAt
    }
  }
`

const MARK_READ = gql`
  mutation MarkTicketMessagesRead($ticketId: ID!) {
    markTicketMessagesRead(ticketId: $ticketId)
  }
`

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_META = {
  OPEN:                 { label: 'Open',                bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  AWAITING_ADMIN:       { label: 'Awaiting Admin',      bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
  AWAITING_RESTAURANT:  { label: 'Awaiting Restaurant', bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  AWAITING_CUSTOMER:    { label: 'Awaiting Customer',   bg: '#ede9fe', text: '#5b21b6', dot: '#7c3aed' },
  RESOLVED:             { label: 'Resolved',            bg: '#dcfce7', text: '#166534', dot: '#16a34a' },
  CLOSED_NO_ACTION:     { label: 'Closed',              bg: '#f1f5f9', text: '#475569', dot: '#94a3b8' },
}

const QUEUE_META = {
  RESTAURANT: { label: 'Restaurant', bg: '#ede9fe', text: '#5b21b6' },
  ADMIN:      { label: 'Admin',      bg: '#e0f2fe', text: '#075985' },
}

const STATUS_SORT_ORDER = {
  AWAITING_ADMIN: 0,
  OPEN: 1,
  AWAITING_RESTAURANT: 2,
  AWAITING_CUSTOMER: 3,
  RESOLVED: 4,
  CLOSED_NO_ACTION: 5,
}

// Whose fault decides who pays and where credit can be spent (see
// rushmyfood-backend/src/utils/ticketRefunds.js).
const FAULT_OPTIONS = [
  { value: 'RESTAURANT',      label: 'Restaurant',       hint: 'Store credit at this restaurant only. The restaurant pays the refund and bonus.' },
  { value: 'PLATFORM',        label: 'RushMyFood',       hint: 'Credit usable at any restaurant. RushMyFood pays.' },
  { value: 'DRIVER',          label: 'Driver',           hint: 'Credit usable at any restaurant. RushMyFood pays.' },
  { value: 'THIRD_PARTY_DSP', label: 'Delivery partner', hint: 'Credit usable at any restaurant. RushMyFood pays.' },
]
const FAULT_LABEL = Object.fromEntries(FAULT_OPTIONS.map(f => [f.value, f.label]))

// Refund states a decision can still be made from (DENIED can be reopened).
const DECIDABLE_REFUND = ['PENDING_ADMIN', 'PENDING_RESTAURANT', 'DENIED']

const CARD_STATUS_META = {
  PENDING:            { label: 'Card refund starting',          color: '#475569' },
  SENDING:            { label: 'Card refund sending',           color: '#475569' },
  WAITING_SETTLEMENT: { label: 'Card refund waits for the charge to settle', color: '#92400e' },
  REFUNDED:           { label: 'Refunded to card',              color: '#166534' },
  FAILED:             { label: 'Card refund failed',            color: '#991b1b' },
}

const money = v => `$${(parseFloat(v) || 0).toFixed(2)}`

function bonusPreview(amount, setting) {
  const value = parseFloat(setting?.value) || 0
  if (!(amount > 0) || value <= 0) return 0
  return setting.type === 'PERCENT' ? Math.round(amount * value) / 100 : value
}

const ISSUE_LABELS = {
  MISSING_ITEM: 'Missing Item', INCORRECT_ITEM: 'Incorrect Item',
  WRONG_ORDER: 'Wrong Order', FOOD_QUALITY: 'Food Quality',
  NEVER_ARRIVED: 'Never Arrived', WRONG_ADDRESS_DROP: 'Wrong Drop',
  DRIVER_ISSUE: 'Driver Issue', ORDER_LATE: 'Order Late',
  TECH_BILLING: 'Tech/Billing', OTHER: 'Other',
  // Rider-raised issues
  PAYOUT_ISSUE: 'Payout / Earnings', APP_TECH_ISSUE: 'App / Technical',
  ORDER_PICKUP_ISSUE: 'Order Pickup', CUSTOMER_UNREACHABLE: 'Customer Unreachable',
  ACCOUNT_ISSUE: 'Account Issue',
}

// Requester badge: distinguishes rider-raised tickets from customer tickets
const REQUESTER_META = {
  CUSTOMER: { label: 'CUSTOMER', color: '#7c3aed' },
  RIDER:    { label: 'RIDER',    color: '#0369a1' },
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.OPEN
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.1, py: 0.4, borderRadius: '20px', bgcolor: m.bg, flexShrink: 0 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: m.dot, flexShrink: 0 }} />
      <Typography fontSize="0.7rem" fontWeight={700} color={m.text} sx={{ whiteSpace: 'nowrap' }}>{m.label}</Typography>
    </Box>
  )
}

function QueueBadge({ queue }) {
  const m = QUEUE_META[queue] || QUEUE_META.ADMIN
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.1, py: 0.4, borderRadius: '20px', bgcolor: m.bg, flexShrink: 0 }}>
      <Typography fontSize="0.7rem" fontWeight={700} color={m.text} sx={{ whiteSpace: 'nowrap' }}>{m.label}</Typography>
    </Box>
  )
}

function formatTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
}

function timeAgo(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

// ─── Ticket Drawer ────────────────────────────────────────────────────────────

function TicketDrawer({ ticket, open, onClose, onRefresh }) {
  const [orderModalOpen, setOrderModalOpen] = useState(false)
  const [fetchOrder, { data: orderData, loading: orderLoading }] = useLazyQuery(GET_ORDER_BY_ID)

  const [msgText, setMsgText] = useState('')
  const [overrideOpen, setOverrideOpen] = useState(false)
  const [overrideAmt, setOverrideAmt] = useState('')
  const [overrideNote, setOverrideNote] = useState('')
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewDecision, setReviewDecision] = useState('APPROVED')
  const [reviewAmt, setReviewAmt] = useState('')
  const [reviewNote, setReviewNote] = useState('')
  const [reviewFault, setReviewFault] = useState('')
  const [reviewError, setReviewError] = useState('')
  const [choiceConfirm, setChoiceConfirm] = useState(null)
  const [actionError, setActionError] = useState('')
  const [optimisticMessages, setOptimisticMessages] = useState([])
  const [closeNote, setCloseNote] = useState('')
  const [closeConfirm, setCloseConfirm] = useState(null)
  const messagesEndRef = React.useRef(null)

  // Clear optimistic messages when server data catches up
  React.useEffect(() => {
    const serverIds = new Set((ticket?.messages || []).map(m => m.id))
    setOptimisticMessages(prev => prev.filter(m => !serverIds.has(m.id) && m.id?.toString().startsWith('tmp-')))
  }, [ticket?.messages?.length])

  const messages = [...(ticket?.messages || []), ...optimisticMessages]
  React.useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages.length])

  const handleViewOrder = () => { fetchOrder({ variables: { id: ticket.orderId } }); setOrderModalOpen(true) }

  const [sendMsg, { loading: sendingMsg }] = useMutation(ADMIN_MESSAGE)
  const [adminReview, { loading: reviewing }] = useMutation(ADMIN_REVIEW)
  const [adminOverride, { loading: overriding }] = useMutation(ADMIN_OVERRIDE)
  const [adminClose, { loading: closing }] = useMutation(ADMIN_CLOSE)
  const [recordChoice, { loading: recordingChoice }] = useMutation(ADMIN_RECORD_CHOICE)
  const [retryCard, { loading: retrying }] = useMutation(ADMIN_RETRY_CARD)
  const [markRead] = useMutation(MARK_READ)
  const { data: bonusData } = useQuery(REFUND_BONUS, { skip: !open })

  // Auto-mark customer/restaurant/rider messages as read when drawer opens
  React.useEffect(() => {
    if (open && ticket?.id) {
      const hasUnread = (ticket.messages || []).some(
        m => !m.isRead && (m.senderType === 'CUSTOMER' || m.senderType === 'RESTAURANT' || m.senderType === 'RIDER')
      )
      if (hasUnread) {
        markRead({ variables: { ticketId: ticket.id } }).then(() => onRefresh())
      }
    }
  }, [open, ticket?.id])

  const handleClose = async () => {
    await adminClose({ variables: { ticketId: ticket.id, resolution: closeConfirm, note: closeNote || null } })
    setCloseConfirm(null); setCloseNote(''); onRefresh()
  }

  if (!ticket) return null

  const refund = ticket.refundRequest
  const isResolved = ticket.status === 'RESOLVED' || ticket.status === 'CLOSED_NO_ACTION'
  const isRider = ticket.requesterType === 'RIDER'

  const handleSendMessage = async (isInternal) => {
    if (!msgText.trim()) return
    const text = msgText.trim(); setMsgText('')
    const tmpId = `tmp-${Date.now()}`
    const optimistic = { id: tmpId, senderType: 'ADMIN', senderName: 'RushMyFood', message: text, isInternal, isRead: false, createdAt: new Date().toISOString() }
    setOptimisticMessages(prev => [...prev, optimistic])
    await sendMsg({ variables: { ticketId: ticket.id, message: text, isInternal } })
    setOptimisticMessages(prev => prev.filter(m => m.id !== tmpId))
    onRefresh()
  }

  const handleReview = async () => {
    setReviewError('')
    try {
      await adminReview({ variables: { ticketId: ticket.id, decision: reviewDecision, approvedAmount: reviewAmt ? parseFloat(reviewAmt) : null, note: reviewNote, faultParty: reviewFault || null } })
      setReviewOpen(false); onRefresh()
    } catch (e) {
      setReviewError(e.message.replace(/^GraphQL error: /, ''))
    }
  }

  const handleRecordChoice = async () => {
    setActionError('')
    try {
      await recordChoice({ variables: { ticketId: ticket.id, choice: choiceConfirm } })
      setChoiceConfirm(null); onRefresh()
    } catch (e) {
      setChoiceConfirm(null)
      setActionError(e.message.replace(/^GraphQL error: /, ''))
    }
  }

  const handleRetryCard = async () => {
    setActionError('')
    try {
      await retryCard({ variables: { ticketId: ticket.id } })
      onRefresh()
    } catch (e) {
      setActionError(e.message.replace(/^GraphQL error: /, ''))
    }
  }

  const handleOverride = async () => {
    await adminOverride({ variables: { ticketId: ticket.id, amount: parseFloat(overrideAmt), note: overrideNote } })
    setOverrideOpen(false); onRefresh()
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 520 }, p: 0, bgcolor: '#fff', display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e2e8f0' } }}>

      {/* Header */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap">
              <Typography fontWeight={800} fontSize="1rem" color="#0f172a">{ticket.ticketNumber}</Typography>
              {isRider && (
                <Box sx={{ px: 1, py: 0.35, borderRadius: '20px', bgcolor: '#e0f2fe', flexShrink: 0 }}>
                  <Typography fontSize="0.68rem" fontWeight={800} color="#0369a1" letterSpacing={0.5}>RIDER</Typography>
                </Box>
              )}
              <QueueBadge queue={ticket.assignedQueue} />
              <StatusBadge status={ticket.status} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
              <Typography fontSize="0.75rem" color="#64748b">{ISSUE_LABELS[ticket.issueType] || ticket.issueType}</Typography>
              {ticket.orderId && (
                <>
                  <Typography fontSize="0.75rem" color="#cbd5e1">·</Typography>
                  <Typography
                    fontSize="0.75rem" fontWeight={600} color="#2563eb"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', '&:hover': { color: '#1d4ed8', textDecoration: 'underline' } }}
                    onClick={handleViewOrder}
                  >
                    Order {ticket.orderDisplayId || `#${ticket.orderId}`} <OpenInNewIcon sx={{ fontSize: 11 }} />
                  </Typography>
                </>
              )}
              <Typography fontSize="0.75rem" color="#cbd5e1">·</Typography>
              <Typography fontSize="0.75rem" color="#94a3b8">{timeAgo(ticket.createdAt)}</Typography>
            </Stack>
          </Box>
          <IconButton onClick={onClose} size="small"
            sx={{ ml: 1, color: '#94a3b8', borderRadius: '8px', '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>

        {/* Requester (Customer or Rider) + Refund */}
        <Stack direction="row" spacing={1.5} mb={2.5}>
          {isRider ? (
            <Box sx={{ flex: 1, p: 1.5, borderRadius: '10px', bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                <PersonOutlineIcon sx={{ fontSize: 13, color: '#0369a1' }} />
                <Typography fontSize="0.68rem" fontWeight={800} color="#0369a1" letterSpacing={0.6}>RIDER</Typography>
              </Stack>
              <Typography fontSize="0.86rem" fontWeight={700} color="#0f172a">{ticket.rider?.name || `Rider #${ticket.riderId}`}</Typography>
              {ticket.rider?.phone && <Typography fontSize="0.72rem" color="#64748b" mt={0.3}>{ticket.rider.phone}</Typography>}
            </Box>
          ) : (
            <Box sx={{ flex: 1, p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                <PersonOutlineIcon sx={{ fontSize: 13, color: '#7c3aed' }} />
                <Typography fontSize="0.68rem" fontWeight={800} color="#7c3aed" letterSpacing={0.6}>CUSTOMER</Typography>
              </Stack>
              <Typography fontSize="0.86rem" fontWeight={700} color="#0f172a">{ticket.customer?.name || `User #${ticket.userId}`}</Typography>
              {ticket.customer?.email && <Typography fontSize="0.72rem" color="#64748b" mt={0.3}>{ticket.customer.email}</Typography>}
              {ticket.customer?.phone && <Typography fontSize="0.72rem" color="#64748b">{ticket.customer.phone}</Typography>}
            </Box>
          )}
          {refund && (
            <Box sx={{ flex: 1, p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                <ReceiptLongIcon sx={{ fontSize: 13, color: '#d97706' }} />
                <Typography fontSize="0.68rem" fontWeight={800} color="#d97706" letterSpacing={0.6}>REFUND</Typography>
              </Stack>
              <Typography fontSize="0.86rem" fontWeight={800} color="#0f172a">${parseFloat(refund.requestedAmount).toFixed(2)}</Typography>
              {refund.approvedAmount != null && (
                <Typography fontSize="0.72rem" color="#16a34a" fontWeight={600} mt={0.3}>Approved: ${parseFloat(refund.approvedAmount).toFixed(2)}</Typography>
              )}
              <Typography fontSize="0.72rem" color="#64748b" mt={0.3}>{refund.status?.replace(/_/g, ' ')}</Typography>
              {refund.faultParty && (
                <Typography fontSize="0.72rem" color="#64748b" mt={0.2}>
                  Fault: <strong style={{ color: '#374151' }}>{FAULT_LABEL[refund.faultParty] || refund.faultParty}</strong>
                </Typography>
              )}
              {refund.restaurantDecision && (
                <Typography fontSize="0.72rem" color="#64748b" mt={0.2}>
                  Restaurant: <strong style={{ color: '#374151' }}>{refund.restaurantDecision}</strong>
                  {refund.restaurantDecisionNote && ` — ${refund.restaurantDecisionNote}`}
                </Typography>
              )}
            </Box>
          )}
        </Stack>

        {/* Conversation */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography fontSize="0.68rem" fontWeight={800} letterSpacing={0.8} color="#94a3b8">CONVERSATION</Typography>
          <Typography fontSize="0.68rem" color="#cbd5e1">{messages.length} message{messages.length !== 1 ? 's' : ''}</Typography>
        </Stack>

        <Box sx={{ bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', p: 1.5, mb: 2, maxHeight: 360, overflowY: 'auto' }}>
          {/* Initial report bubble */}
          {(() => {
            let items = null
            try { const raw = refund?.affectedItems; items = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null } catch (e) {}
            const evidenceUrls = ticket.evidenceUrls || []
            return (
              <Box sx={{ mb: 1.5, p: 1.5, borderRadius: '10px', bgcolor: '#faf5ff', border: '1px solid #e9d5ff', borderLeftWidth: 3, borderLeftColor: '#7c3aed' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#7c3aed', flexShrink: 0 }} />
                    <Typography fontSize="0.72rem" fontWeight={700} color="#5b21b6">{isRider ? (ticket.rider?.name || 'Rider') : (ticket.customer?.name || 'Customer')}</Typography>
                    <Box sx={{ px: 0.6, py: 0.15, bgcolor: '#ede9fe', borderRadius: '4px' }}>
                      <Typography fontSize="0.6rem" fontWeight={800} color="#5b21b6" letterSpacing={0.4}>INITIAL REPORT</Typography>
                    </Box>
                  </Stack>
                  <Typography fontSize="0.68rem" color="#94a3b8">{formatTime(ticket.createdAt)}</Typography>
                </Stack>
                <Typography fontSize="0.82rem" color="#374151" mb={0.3}><span style={{ color: '#6b7280', fontSize: '0.75rem' }}>Issue: </span>{ISSUE_LABELS[ticket.issueType] || ticket.issueType}</Typography>
                {ticket.description && <Typography fontSize="0.82rem" color="#374151" mb={0.3}><span style={{ color: '#6b7280', fontSize: '0.75rem' }}>Description: </span>{ticket.description}</Typography>}
                {refund && <Typography fontSize="0.82rem" color="#374151" mb={items?.length > 0 ? 0.3 : 0}><span style={{ color: '#6b7280', fontSize: '0.75rem' }}>Credit requested: </span><strong style={{ color: '#7c3aed' }}>${parseFloat(refund.requestedAmount).toFixed(2)}</strong></Typography>}
                {items?.length > 0 && (
                  <Box mt={0.75} pt={0.75} sx={{ borderTop: '1px solid #e9d5ff' }}>
                    {items.map((item, i) => (
                      <Stack key={i} direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.4}>
                        <Box>
                          <Typography fontSize="0.78rem" fontWeight={600} color="#374151">{item.name}</Typography>
                          <Typography fontSize="0.68rem" color="#6b7280">Ordered: {item.qty} · Missing: {item.refundQty}</Typography>
                        </Box>
                        <Typography fontSize="0.78rem" fontWeight={700} color="#7c3aed">${parseFloat(item.refundAmount).toFixed(2)}</Typography>
                      </Stack>
                    ))}
                  </Box>
                )}
                {evidenceUrls.length > 0 && (
                  <Stack direction="row" spacing={0.75} mt={1} flexWrap="wrap">
                    {evidenceUrls.map((url, i) => (
                      <Box key={i} component="a" href={url} target="_blank" rel="noreferrer">
                        <Box component="img" src={url} sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '8px', border: '1px solid #e9d5ff', cursor: 'pointer', '&:hover': { opacity: 0.8 } }} />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )
          })()}

          {/* Messages */}
          {messages.map(m => {
            const isCustomer = m.senderType === 'CUSTOMER'
            const isAdmin = m.senderType === 'ADMIN'
            const isRiderMsg = m.senderType === 'RIDER'
            const internal = m.isInternal
            let bgColor, borderColor, dotColor, nameColor
            if (internal)        { bgColor = '#fff7ed'; borderColor = '#fed7aa'; dotColor = '#f97316'; nameColor = '#c2410c' }
            else if (isAdmin)    { bgColor = '#eff6ff'; borderColor = '#bfdbfe'; dotColor = '#3b82f6'; nameColor = '#1d4ed8' }
            else if (isCustomer) { bgColor = '#faf5ff'; borderColor = '#e9d5ff'; dotColor = '#7c3aed'; nameColor = '#5b21b6' }
            else if (isRiderMsg) { bgColor = '#f0f9ff'; borderColor = '#bae6fd'; dotColor = '#0ea5e9'; nameColor = '#0369a1' }
            else                 { bgColor = '#f0fdf4'; borderColor = '#bbf7d0'; dotColor = '#16a34a'; nameColor = '#166534' }
            const displayName = m.senderName || (isAdmin ? 'RushMyFood' : isCustomer ? 'Customer' : isRiderMsg ? 'Rider' : 'Restaurant')
            const evidenceUrls = m.evidenceUrls ? (Array.isArray(m.evidenceUrls) ? m.evidenceUrls : JSON.parse(m.evidenceUrls)) : []
            return (
              <Box key={m.id} sx={{
                mb: 1.2, p: 1.5, borderRadius: '10px',
                bgcolor: bgColor, border: `1px solid ${borderColor}`,
                borderLeftWidth: 3, borderLeftColor: dotColor,
                opacity: m.id?.toString().startsWith('tmp-') ? 0.55 : 1,
                transition: 'opacity 0.3s',
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                    <Typography fontSize="0.72rem" fontWeight={700} color={nameColor}>
                      {displayName}{m.id?.toString().startsWith('tmp-') ? ' — sending…' : ''}
                    </Typography>
                    {internal && (
                      <Box sx={{ px: 0.6, py: 0.15, bgcolor: '#fed7aa', borderRadius: '4px' }}>
                        <Typography fontSize="0.58rem" fontWeight={800} color="#9a3412" letterSpacing={0.4}>INTERNAL</Typography>
                      </Box>
                    )}
                    {isCustomer && m.issueType && (
                      <Box sx={{ px: 0.6, py: 0.15, bgcolor: '#ede9fe', borderRadius: '4px' }}>
                        <Typography fontSize="0.58rem" fontWeight={700} color="#5b21b6">{m.issueType.replace(/_/g, ' ')}</Typography>
                      </Box>
                    )}
                  </Stack>
                  <Typography fontSize="0.68rem" color="#94a3b8">{formatTime(m.createdAt)}</Typography>
                </Stack>
                <Typography fontSize="0.83rem" color="#374151" sx={{ whiteSpace: 'pre-line', lineHeight: 1.55 }}>{m.message}</Typography>
                {evidenceUrls.length > 0 && (
                  <Stack direction="row" spacing={0.75} mt={0.75} flexWrap="wrap">
                    {evidenceUrls.map((url, i) => (
                      <Box key={i} component="a" href={url} target="_blank" rel="noreferrer">
                        <Box component="img" src={url} sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '8px', border: `1px solid ${borderColor}`, cursor: 'pointer', '&:hover': { opacity: 0.8 } }} />
                      </Box>
                    ))}
                  </Stack>
                )}
                {isAdmin && !m.id?.toString().startsWith('tmp-') && (
                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: m.isRead ? '#16a34a' : '#94a3b8', flexShrink: 0 }} />
                    <Typography fontSize="0.62rem" color={m.isRead ? '#16a34a' : '#94a3b8'} fontWeight={600}>
                      {m.isRead ? 'Seen' : 'Sent'}
                    </Typography>
                  </Box>
                )}
              </Box>
            )
          })}
          <div ref={messagesEndRef} />
        </Box>

        {/* Reply box */}
        {!isResolved && (
          <Box mb={2.5}>
            <TextField
              fullWidth size="small" multiline maxRows={3}
              placeholder="Type a message…"
              value={msgText} onChange={e => setMsgText(e.target.value)}
              sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem', bgcolor: '#fff', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#cbd5e1' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" fullWidth
                onClick={() => handleSendMessage(false)}
                disabled={sendingMsg || !msgText.trim()}
                startIcon={<SendIcon sx={{ fontSize: '0.82rem !important' }} />}
                sx={{ borderRadius: '8px', bgcolor: '#1d4ed8', fontSize: '0.75rem', textTransform: 'none', fontWeight: 700, py: 0.85, '&:hover': { bgcolor: '#1e40af' } }}>
                {isRider ? 'Send to Rider' : 'Send to Customer'}
              </Button>
              {!isRider && (
                <Button variant="outlined" size="small" fullWidth
                  onClick={() => handleSendMessage(true)}
                  disabled={sendingMsg || !msgText.trim()}
                  sx={{ borderRadius: '8px', borderColor: '#fde68a', color: '#92400e', bgcolor: '#fffbeb', fontSize: '0.75rem', textTransform: 'none', fontWeight: 700, py: 0.85, '&:hover': { bgcolor: '#fef3c7', borderColor: '#fcd34d' } }}>
                  Send to Restaurant
                </Button>
              )}
            </Stack>
          </Box>
        )}

        <Divider sx={{ borderColor: '#f1f5f9', mb: 2 }} />

        {/* How the approved refund is coming back */}
        {refund?.approvedAmount != null && refund.status !== 'DENIED' && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography fontSize="0.68rem" fontWeight={800} letterSpacing={0.6} color="#64748b" mb={0.75}>REFUND CHOICE</Typography>
            {refund.status === 'AWAITING_CUSTOMER' ? (
              <>
                <Typography fontSize="0.8rem" color="#374151" mb={1}>
                  Waiting for the customer to choose between:
                </Typography>
                <Typography fontSize="0.78rem" color="#374151">
                  • Original payment — {refund.walletRefundAmount > 0 ? `${money(refund.walletRefundAmount)} to their wallet and ` : ''}{money(refund.cardRefundAmount)} to {refund.cardLabel || 'their card'}
                </Typography>
                <Typography fontSize="0.78rem" color="#374151" mb={1.25}>
                  • {money(refund.creditTotal)} {refund.creditScope === 'RESTAURANT' ? `store credit at ${refund.creditRestaurantName || 'the restaurant'}` : 'credit usable anywhere'}{refund.bonusAmount > 0 ? ` (includes ${money(refund.bonusAmount)} bonus)` : ''}
                </Typography>
                <Typography fontSize="0.72rem" color="#64748b" mb={0.75}>Customer told you their choice? Record it for them:</Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={() => setChoiceConfirm('ORIGINAL')}
                    sx={{ flex: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.74rem' }}>
                    Original payment
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setChoiceConfirm('CREDIT')}
                    sx={{ flex: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.74rem' }}>
                    Credit
                  </Button>
                </Stack>
              </>
            ) : refund.customerChoice ? (
              <>
                <Typography fontSize="0.8rem" color="#374151">
                  {refund.customerChoice === 'CREDIT'
                    ? `${money(refund.creditTotal)} ${refund.creditScope === 'RESTAURANT' ? `store credit at ${refund.creditRestaurantName || 'the restaurant'}` : 'credit usable anywhere'}${refund.bonusAmount > 0 ? ` (includes ${money(refund.bonusAmount)} bonus)` : ''}`
                    : `Original payment — ${refund.walletRefundAmount > 0 ? `${money(refund.walletRefundAmount)} to wallet` : ''}${refund.walletRefundAmount > 0 && refund.cardRefundAmount > 0 ? ', ' : ''}${refund.cardRefundAmount > 0 ? `${money(refund.cardRefundAmount)} to ${refund.cardLabel || 'card'}` : ''}`}
                </Typography>
                <Typography fontSize="0.7rem" color="#94a3b8" mt={0.3}>
                  {refund.choiceMadeBy === 'CUSTOMER' ? 'Chosen by the customer' : refund.choiceMadeBy === 'ADMIN' ? 'Recorded by an admin' : 'No card to refund, so credit was issued'}
                </Typography>
                {refund.cardRefundStatus && (
                  <Typography fontSize="0.75rem" fontWeight={700} mt={0.75} color={CARD_STATUS_META[refund.cardRefundStatus]?.color || '#475569'}>
                    {CARD_STATUS_META[refund.cardRefundStatus]?.label || refund.cardRefundStatus}
                  </Typography>
                )}
                {refund.cardRefundError && refund.cardRefundStatus !== 'REFUNDED' && (
                  <Typography fontSize="0.72rem" color="#64748b" mt={0.3}>{refund.cardRefundError}</Typography>
                )}
                {['FAILED', 'WAITING_SETTLEMENT'].includes(refund.cardRefundStatus) && (
                  <Button size="small" variant="outlined" onClick={handleRetryCard} disabled={retrying}
                    sx={{ mt: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.74rem' }}>
                    {retrying ? <CircularProgress size={14} /> : 'Retry card refund'}
                  </Button>
                )}
              </>
            ) : null}
            {actionError && <Typography fontSize="0.74rem" color="#b91c1c" mt={1}>{actionError}</Typography>}
          </Box>
        )}

        {/* Action buttons */}
        {!isResolved && (
          <Stack spacing={1}>
            {/* Refund/wallet actions only apply to customer tickets — riders have no refund flow */}
            {!isRider && (!refund || DECIDABLE_REFUND.includes(refund.status)) && (
              <Button fullWidth variant="contained" size="small"
                startIcon={<SupportAgentIcon />}
                onClick={() => { setReviewAmt(refund?.requestedAmount || ''); setReviewFault(''); setReviewDecision('APPROVED'); setReviewError(''); setReviewOpen(true) }}
                sx={{ borderRadius: '8px', fontWeight: 700, bgcolor: '#059669', fontSize: '0.8rem', textTransform: 'none', '&:hover': { bgcolor: '#047857' } }}>
                Review Refund Request
              </Button>
            )}
            {!isRider && (
              <Button fullWidth variant="contained" size="small"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={() => { setOverrideAmt(refund?.requestedAmount || ''); setOverrideOpen(true) }}
                sx={{ borderRadius: '8px', fontWeight: 700, bgcolor: '#7c3aed', fontSize: '0.8rem', textTransform: 'none', '&:hover': { bgcolor: '#6d28d9' } }}>
                Override &amp; Issue Wallet Credit
              </Button>
            )}
            <Stack direction="row" spacing={1}>
              <Button fullWidth variant="outlined" size="small"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => setCloseConfirm('RESOLVED')}
                sx={{ borderRadius: '8px', fontWeight: 700, fontSize: '0.77rem', textTransform: 'none', borderColor: '#16a34a', color: '#16a34a', '&:hover': { bgcolor: '#f0fdf4', borderColor: '#16a34a' } }}>
                Mark Resolved
              </Button>
              <Button fullWidth variant="outlined" size="small"
                startIcon={<DoNotDisturbOnIcon />}
                onClick={() => setCloseConfirm('CLOSED_NO_ACTION')}
                sx={{ borderRadius: '8px', fontWeight: 700, fontSize: '0.77rem', textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b', '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' } }}>
                Close No Action
              </Button>
            </Stack>
          </Stack>
        )}

        {isResolved && (
          <Box sx={{
            p: 2, borderRadius: '10px', textAlign: 'center',
            bgcolor: ticket.status === 'RESOLVED' ? '#f0fdf4' : '#f8fafc',
            border: `1px solid ${ticket.status === 'RESOLVED' ? '#bbf7d0' : '#e2e8f0'}`,
          }}>
            {ticket.status === 'RESOLVED'
              ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 24, mb: 0.5 }} />
              : <DoNotDisturbOnIcon sx={{ color: '#94a3b8', fontSize: 24, mb: 0.5 }} />
            }
            <Typography fontWeight={700} fontSize="0.88rem" color={ticket.status === 'RESOLVED' ? '#166534' : '#475569'}>
              {ticket.status === 'RESOLVED' ? 'Ticket Resolved' : 'Closed — No Action Taken'}
            </Typography>
            {ticket.resolutionNote && <Typography fontSize="0.78rem" color="#6b7280" mt={0.5}>{ticket.resolutionNote}</Typography>}
            {ticket.resolvedAt && <Typography fontSize="0.72rem" color="#94a3b8" mt={0.5}>{formatTime(ticket.resolvedAt)}</Typography>}
          </Box>
        )}
      </Box>

      {/* Override & Credit dialog */}
      <Dialog open={overrideOpen} onClose={() => setOverrideOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '14px', border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', pb: 0 }}>Override &amp; Issue Credit</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography fontSize="0.82rem" color="#64748b" mb={2}>Issue wallet credit directly to the customer, usable at any restaurant. RushMyFood pays it, with no bonus and no choice for the customer.</Typography>
          <TextField label="Amount ($)" type="number" fullWidth size="small" value={overrideAmt}
            onChange={e => setOverrideAmt(e.target.value)} sx={{ mb: 2 }} />
          <TextField label="Internal note (required)" fullWidth size="small" multiline rows={2}
            value={overrideNote} onChange={e => setOverrideNote(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setOverrideOpen(false)} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={!overrideAmt || !overrideNote || overriding} onClick={handleOverride}
            sx={{ bgcolor: '#7c3aed', borderRadius: '8px', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#6d28d9' } }}>
            {overriding ? <CircularProgress size={16} /> : 'Confirm Credit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Refund dialog */}
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '14px', border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', pb: 0 }}>Review Refund Request</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Decision</InputLabel>
            <Select value={reviewDecision} onChange={e => setReviewDecision(e.target.value)} label="Decision">
              <MenuItem value="APPROVED">Approve</MenuItem>
              <MenuItem value="PARTIALLY_APPROVED">Partially Approve</MenuItem>
              <MenuItem value="DENIED">Deny</MenuItem>
            </Select>
          </FormControl>
          {reviewDecision !== 'DENIED' && (
            <TextField label="Approved Amount ($)" type="number" fullWidth size="small"
              value={reviewAmt} onChange={e => setReviewAmt(e.target.value)} sx={{ mb: 2 }} />
          )}
          <FormControl fullWidth size="small" sx={{ mb: reviewFault ? 0.75 : 2 }}>
            <InputLabel>{reviewDecision === 'DENIED' ? 'Whose fault (optional)' : 'Whose fault'}</InputLabel>
            <Select value={reviewFault} onChange={e => setReviewFault(e.target.value)} label={reviewDecision === 'DENIED' ? 'Whose fault (optional)' : 'Whose fault'}>
              {FAULT_OPTIONS.map(f => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {reviewFault && (
            <Typography fontSize="0.74rem" color="#64748b" mb={2}>{FAULT_OPTIONS.find(f => f.value === reviewFault)?.hint}</Typography>
          )}
          {reviewDecision !== 'DENIED' && reviewFault && parseFloat(reviewAmt) > 0 && (() => {
            const amt = parseFloat(reviewAmt)
            const setting = reviewFault === 'RESTAURANT' ? bonusData?.refundBonusSettings?.store : bonusData?.refundBonusSettings?.platform
            const bonus = bonusPreview(amt, setting)
            return (
              <Box sx={{ mb: 2, p: 1.25, borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Typography fontSize="0.74rem" fontWeight={700} color="#374151" mb={0.5}>The customer will choose:</Typography>
                <Typography fontSize="0.74rem" color="#374151">• {money(amt)} back to their original payment (card refunds take 3–8 business days)</Typography>
                <Typography fontSize="0.74rem" color="#374151">
                  • {money(amt + bonus)} {reviewFault === 'RESTAURANT' ? 'store credit at this restaurant' : 'credit usable anywhere'}{bonus > 0 ? ` (${money(bonus)} bonus)` : ''}
                </Typography>
                <Typography fontSize="0.7rem" color="#94a3b8" mt={0.5}>Cash and wallet-only orders skip the choice and get the credit.</Typography>
              </Box>
            )
          })()}
          <TextField label="Note" fullWidth size="small" multiline rows={2}
            value={reviewNote} onChange={e => setReviewNote(e.target.value)} />
          {reviewError && <Typography fontSize="0.76rem" color="#b91c1c" mt={1.25}>{reviewError}</Typography>}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setReviewOpen(false)} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="success" disabled={reviewing || (reviewDecision !== 'DENIED' && (!reviewFault || !(parseFloat(reviewAmt) > 0)))} onClick={handleReview}
            sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}>
            {reviewing ? <CircularProgress size={16} /> : 'Submit Decision'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record the customer's choice */}
      <Dialog open={!!choiceConfirm} onClose={() => setChoiceConfirm(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '14px', border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', pb: 0 }}>
          {choiceConfirm === 'ORIGINAL' ? 'Refund to original payment?' : 'Give credit instead?'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography fontSize="0.82rem" color="#64748b">
            {choiceConfirm === 'ORIGINAL'
              ? `${refund?.walletRefundAmount > 0 ? `${money(refund.walletRefundAmount)} goes back to their wallet and ` : ''}${money(refund?.cardRefundAmount)} is refunded to ${refund?.cardLabel || 'their card'} now. This can't be undone.`
              : `${money(refund?.creditTotal)} is added as ${refund?.creditScope === 'RESTAURANT' ? `store credit at ${refund?.creditRestaurantName || 'the restaurant'}` : 'credit usable anywhere'}. This can't be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setChoiceConfirm(null)} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={recordingChoice} onClick={handleRecordChoice}
            sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}>
            {recordingChoice ? <CircularProgress size={16} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close ticket dialog */}
      <Dialog open={!!closeConfirm} onClose={() => setCloseConfirm(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '14px', border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', pb: 0 }}>
          {closeConfirm === 'RESOLVED' ? 'Mark Ticket Resolved' : 'Close Without Action'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography fontSize="0.82rem" color="#64748b" mb={2}>
            {closeConfirm === 'RESOLVED'
              ? 'Mark this ticket as resolved. The customer will be notified.'
              : 'Close this ticket without issuing a credit. The customer will see their request was declined.'}
          </Typography>
          <TextField label="Note (optional)" fullWidth size="small" multiline rows={2}
            value={closeNote} onChange={e => setCloseNote(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => { setCloseConfirm(null); setCloseNote('') }} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={closing}
            color={closeConfirm === 'RESOLVED' ? 'success' : 'error'}
            onClick={handleClose}
            sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}>
            {closing ? <CircularProgress size={16} /> : closeConfirm === 'RESOLVED' ? 'Confirm Resolved' : 'Confirm Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order detail modal */}
      <Modal open={orderModalOpen} onClose={() => setOrderModalOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, zIndex: 1400 }}>
        <Box sx={{ bgcolor: 'white', borderRadius: 2, width: { xs: '100%', sm: '85%', md: '65%', lg: '50%' }, maxHeight: '90vh', overflowY: 'auto', outline: 'none' }}>
          {orderLoading && <Box p={4} textAlign="center"><CircularProgress /></Box>}
          {!orderLoading && orderData?.getOrderById && (
            <OrderComponent order={orderData.getOrderById} onClose={() => setOrderModalOpen(false)} onStatusChange={() => {}} availableRiders={[]} />
          )}
          {!orderLoading && !orderData?.getOrderById && (
            <Box p={4} textAlign="center"><Typography color="text.secondary">Order not found.</Typography></Box>
          )}
        </Box>
      </Modal>
    </Drawer>
  )
}

// ─── Refund bonus settings ────────────────────────────────────────────────────

// The bonus a customer gets for taking a refund as credit instead of back to
// their card. Separate for platform credit and store credit.
function RefundBonusDialog({ open, onClose }) {
  const { data, loading } = useQuery(REFUND_BONUS, { skip: !open, fetchPolicy: 'network-only' })
  const [save, { loading: saving }] = useMutation(SAVE_REFUND_BONUS)
  const [form, setForm] = useState(null)
  const [error, setError] = useState('')

  React.useEffect(() => {
    const b = data?.refundBonusSettings
    if (open && b) {
      setForm({
        platformType: b.platform.type, platformValue: String(b.platform.value),
        storeType: b.store.type, storeValue: String(b.store.value),
      })
      setError('')
    }
  }, [open, data])

  const handleSave = async () => {
    setError('')
    try {
      await save({ variables: {
        platformType: form.platformType, platformValue: parseFloat(form.platformValue) || 0,
        storeType: form.storeType, storeValue: parseFloat(form.storeValue) || 0,
      } })
      onClose()
    } catch (e) {
      setError(e.message.replace(/^GraphQL error: /, ''))
    }
  }

  const row = (label, hint, typeKey, valueKey) => (
    <Box mb={2}>
      <Typography fontSize="0.82rem" fontWeight={700} color="#0f172a">{label}</Typography>
      <Typography fontSize="0.74rem" color="#64748b" mb={1}>{hint}</Typography>
      <Stack direction="row" spacing={1}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select value={form[typeKey]} onChange={e => setForm({ ...form, [typeKey]: e.target.value })}>
            <MenuItem value="FLAT">Flat amount</MenuItem>
            <MenuItem value="PERCENT">Percentage</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" type="number" value={form[valueKey]}
          onChange={e => setForm({ ...form, [valueKey]: e.target.value })}
          inputProps={{ min: 0, step: form[typeKey] === 'PERCENT' ? 1 : 0.5 }}
          InputProps={form[typeKey] === 'PERCENT'
            ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
            : { startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          sx={{ flex: 1 }} />
      </Stack>
    </Box>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '14px', border: '1px solid #e2e8f0' } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', pb: 0 }}>Refund bonus</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography fontSize="0.82rem" color="#64748b" mb={2}>
          Extra credit a customer gets when they take an approved refund as credit instead of back to their card. Set to 0 for no bonus.
        </Typography>
        {loading || !form ? (
          <Box py={3} textAlign="center"><CircularProgress size={22} /></Box>
        ) : (
          <>
            {row('Platform credit', 'RushMyFood, driver or delivery partner at fault. Usable at any restaurant, RushMyFood pays.', 'platformType', 'platformValue')}
            {row('Store credit', 'Restaurant at fault. Usable only at that restaurant, the restaurant pays.', 'storeType', 'storeValue')}
          </>
        )}
        {error && <Typography fontSize="0.76rem" color="#b91c1c">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disabled={saving || !form} onClick={handleSave}
          sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}>
          {saving ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function SupportTickets() {
  const [queueFilter, setQueueFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('OPEN') // default to open tickets on load
  const [search, setSearch] = useState('')
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [bonusOpen, setBonusOpen] = useState(false)

  // Filtered query — drives the table
  // When statusFilter==='OPEN' we fetch all and filter client-side (it's a pseudo-filter for AWAITING_ADMIN+AWAITING_RESTAURANT)
  const { data, loading, refetch } = useQuery(ALL_TICKETS, {
    variables: { queue: queueFilter || undefined, status: (statusFilter && statusFilter !== 'OPEN') ? statusFilter : undefined, limit: 50 },
    fetchPolicy: 'cache-and-network',
    pollInterval: 10000,
  })

  // Unfiltered query — drives the stat pills (always shows totals per status)
  const { data: countsData } = useQuery(ALL_TICKETS, {
    variables: { limit: 200 },
    fetchPolicy: 'cache-and-network',
  })

  const all = data?.allSupportTickets || []
  // Derive selectedTicket directly from live query data — always fresh
  const selectedTicket = selectedTicketId ? (all.find(t => t.id === selectedTicketId) || null) : null
  const allForCounts = countsData?.allSupportTickets || []

  // Search + pseudo-filter for "OPEN" pill (shows OPEN + AWAITING_ADMIN + AWAITING_RESTAURANT)
  const filtered = all.filter(t => {
    if (statusFilter === 'OPEN' && t.status !== 'OPEN' && t.status !== 'AWAITING_ADMIN' && t.status !== 'AWAITING_RESTAURANT') return false
    if (!search) return true
    const s = search.toLowerCase()
    return (
      t.ticketNumber.toLowerCase().includes(s) ||
      (t.orderId != null && String(t.orderId).includes(s)) ||
      ISSUE_LABELS[t.issueType]?.toLowerCase().includes(s) ||
      t.customer?.name?.toLowerCase().includes(s) ||
      t.rider?.name?.toLowerCase().includes(s)
    )
  })

  // Sort: unresolved first, then by recency within each group
  const tickets = [...filtered].sort((a, b) => {
    const sa = STATUS_SORT_ORDER[a.status] ?? 99
    const sb = STATUS_SORT_ORDER[b.status] ?? 99
    if (sa !== sb) return sa - sb
    return new Date(b.createdAt) - new Date(a.createdAt)
  })

  const unresolvedCount = allForCounts.filter(t => t.status !== 'RESOLVED' && t.status !== 'CLOSED_NO_ACTION').length
  const statCounts = {
    OPEN:                allForCounts.filter(t => t.status === 'AWAITING_ADMIN' || t.status === 'AWAITING_RESTAURANT' || t.status === 'OPEN').length,
    AWAITING_ADMIN:      allForCounts.filter(t => t.status === 'AWAITING_ADMIN').length,
    AWAITING_RESTAURANT: allForCounts.filter(t => t.status === 'AWAITING_RESTAURANT').length,
    AWAITING_CUSTOMER:   allForCounts.filter(t => t.status === 'AWAITING_CUSTOMER').length,
    RESOLVED:            allForCounts.filter(t => t.status === 'RESOLVED').length,
    CLOSED_NO_ACTION:    allForCounts.filter(t => t.status === 'CLOSED_NO_ACTION').length,
  }

  const openDrawer = (ticket) => { setSelectedTicketId(ticket.id); setDrawerOpen(true) }
  const hasActiveFilter = !!(search || queueFilter || statusFilter)

  const columns = [
    {
      name: 'TICKET', width: '160px',
      cell: r => {
        const unread = (r.messages || []).filter(
          m => !m.isRead && (m.senderType === 'CUSTOMER' || m.senderType === 'RESTAURANT' || m.senderType === 'RIDER')
        ).length
        return (
          <Box sx={{ cursor: 'pointer', py: 0.5 }} onClick={() => openDrawer(r)}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography fontSize="0.82rem" fontWeight={700} color="#2563eb">{r.ticketNumber}</Typography>
              {unread > 0 && (
                <Box sx={{ px: 0.7, py: 0.15, bgcolor: '#ef4444', borderRadius: '10px', flexShrink: 0 }}>
                  <Typography fontSize="0.6rem" fontWeight={800} color="#fff" lineHeight={1.4}>
                    {unread} new
                  </Typography>
                </Box>
              )}
            </Stack>
            <Typography fontSize="0.7rem" color="#94a3b8" mt={0.2}>{timeAgo(r.createdAt)}</Typography>
          </Box>
        )
      },
    },
    {
      name: 'REQUESTER / ISSUE', grow: 1,
      cell: r => {
        const rider = r.requesterType === 'RIDER'
        return (
          <Box sx={{ py: 0.5, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography fontSize="0.82rem" fontWeight={600} color="#0f172a" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {rider ? (r.rider?.name || `Rider #${r.riderId}`) : (r.customer?.name || `User #${r.userId}`)}
              </Typography>
              {rider && (
                <Box sx={{ px: 0.6, py: 0.1, bgcolor: '#e0f2fe', borderRadius: '4px', flexShrink: 0 }}>
                  <Typography fontSize="0.58rem" fontWeight={800} color="#0369a1" letterSpacing={0.3}>RIDER</Typography>
                </Box>
              )}
            </Stack>
            <Typography fontSize="0.72rem" color="#64748b">{ISSUE_LABELS[r.issueType] || r.issueType}</Typography>
          </Box>
        )
      },
    },
    {
      name: 'ORDER', hide: 'md', width: '82px',
      cell: r => <Typography fontSize="0.8rem" color="#64748b">{r.orderId ? (r.orderDisplayId || `#${r.orderId}`) : '—'}</Typography>,
    },
    {
      name: 'QUEUE', hide: 'md', width: '120px',
      cell: r => <QueueBadge queue={r.assignedQueue} />,
    },
    {
      name: 'STATUS', width: '170px',
      cell: r => <StatusBadge status={r.status} />,
    },
    {
      name: 'REQUESTED', hide: 'sm', width: '106px',
      cell: r => r.refundRequest
        ? <Typography fontSize="0.82rem" fontWeight={700} color="#d97706">${parseFloat(r.refundRequest.requestedAmount).toFixed(2)}</Typography>
        : <Typography fontSize="0.8rem" color="#cbd5e1">—</Typography>,
    },
    {
      name: 'UPDATED', hide: 'sm', width: '110px',
      cell: r => <Typography fontSize="0.75rem" color="#94a3b8">{timeAgo(r.updatedAt || r.createdAt)}</Typography>,
    },
  ]

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pt: 3, pb: 6 }}>
      <Container maxWidth="xl">

        {/* Page header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2.5}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a" letterSpacing={-0.3}>Support Tickets</Typography>
            <Typography fontSize="0.82rem" color="#64748b" mt={0.25}>
              Unified view — all queues
              {!loading && all.length > 0 && (
                <>&nbsp;·&nbsp;<strong style={{ color: unresolvedCount > 0 ? '#dc2626' : '#16a34a' }}>{unresolvedCount} unresolved</strong>&nbsp;·&nbsp;{all.length} total</>
              )}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="outlined" onClick={() => setBonusOpen(true)}
            startIcon={<AccountBalanceWalletIcon sx={{ fontSize: '1rem !important' }} />}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: '9px', color: '#374151', borderColor: '#e2e8f0', bgcolor: '#fff', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}>
            Refund bonus
          </Button>
          <Tooltip title="Refresh" placement="left">
            <span>
              <IconButton onClick={() => refetch()} disabled={loading}
                sx={{ color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: '9px', bgcolor: '#fff', '&:hover': { color: '#0f172a', bgcolor: '#f8fafc', borderColor: '#cbd5e1' } }}>
                {loading ? <CircularProgress size={16} sx={{ color: '#94a3b8' }} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          </Stack>
        </Stack>

        {/* KPI stat pills — always show totals regardless of active filter */}
        <Stack direction="row" spacing={1} mb={2.5} flexWrap="wrap" useFlexGap>
          {/* "Open" pill = combined AWAITING_ADMIN + AWAITING_RESTAURANT */}
          {[
            { key: 'OPEN',                dot: '#ef4444', bg: '#fee2e2', text: '#991b1b', label: 'Open' },
            { key: 'AWAITING_ADMIN',      dot: STATUS_META.AWAITING_ADMIN.dot,      bg: STATUS_META.AWAITING_ADMIN.bg,      text: STATUS_META.AWAITING_ADMIN.text,      label: STATUS_META.AWAITING_ADMIN.label },
            { key: 'AWAITING_CUSTOMER',   dot: STATUS_META.AWAITING_CUSTOMER.dot,   bg: STATUS_META.AWAITING_CUSTOMER.bg,   text: STATUS_META.AWAITING_CUSTOMER.text,   label: STATUS_META.AWAITING_CUSTOMER.label },
            { key: 'RESOLVED',            dot: STATUS_META.RESOLVED.dot,            bg: STATUS_META.RESOLVED.bg,            text: STATUS_META.RESOLVED.text,            label: STATUS_META.RESOLVED.label },
            { key: 'CLOSED_NO_ACTION',    dot: STATUS_META.CLOSED_NO_ACTION.dot,    bg: STATUS_META.CLOSED_NO_ACTION.bg,    text: STATUS_META.CLOSED_NO_ACTION.text,    label: STATUS_META.CLOSED_NO_ACTION.label },
          ].map(({ key, dot, bg, text, label }) => (
            <Box key={key} onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.4, py: 0.65,
                borderRadius: '20px', cursor: 'pointer', userSelect: 'none',
                border: statusFilter === key ? `1.5px solid ${dot}` : '1.5px solid #e2e8f0',
                bgcolor: statusFilter === key ? bg : '#fff',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: dot, bgcolor: bg },
              }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />
              <Typography fontSize="0.75rem" fontWeight={600} color={statusFilter === key ? text : '#374151'}>
                {statCounts[key]} {label}
              </Typography>
            </Box>
          ))}
          <Box onClick={() => setStatusFilter('')}
            sx={{
              display: 'inline-flex', alignItems: 'center', px: 1.4, py: 0.65,
              borderRadius: '20px', cursor: 'pointer', userSelect: 'none',
              border: !statusFilter ? '1.5px solid #0f172a' : '1.5px solid #e2e8f0',
              bgcolor: !statusFilter ? '#f1f5f9' : '#fff',
              transition: 'all 0.15s ease',
              '&:hover': { borderColor: '#64748b', bgcolor: '#f8fafc' },
            }}>
            <Typography fontSize="0.75rem" fontWeight={600} color={!statusFilter ? '#0f172a' : '#374151'}>
              {allForCounts.length} All
            </Typography>
          </Box>
        </Stack>

        {/* Filter bar */}
        <Paper elevation={0} sx={{ p: 1.75, mb: hasActiveFilter ? 1 : 2, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems="center">
            <TextField
              size="small"
              placeholder="Search ticket #, order #, customer, issue…"
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment> }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '9px', fontSize: '0.84rem', bgcolor: '#f8fafc',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                  '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 152 }}>
              <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} displayEmpty
                sx={{ borderRadius: '9px', fontSize: '0.83rem', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#cbd5e1' } }}
                renderValue={v => v
                  ? <Stack direction="row" alignItems="center" spacing={0.75}><Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: STATUS_META[v]?.dot, flexShrink: 0 }} /><span style={{ fontSize: '0.82rem' }}>{STATUS_META[v]?.label}</span></Stack>
                  : <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>All Statuses</span>
                }>
                <MenuItem value=""><em style={{ fontStyle: 'normal', color: '#64748b', fontSize: '0.83rem' }}>All Statuses</em></MenuItem>
                {Object.entries(STATUS_META).filter(([key]) => key !== 'AWAITING_RESTAURANT').map(([key, m]) => (
                  <MenuItem key={key} value={key}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: m.dot, flexShrink: 0 }} />
                      <Typography fontSize="0.83rem">{m.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select value={queueFilter} onChange={e => setQueueFilter(e.target.value)} displayEmpty
                sx={{ borderRadius: '9px', fontSize: '0.83rem', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#cbd5e1' } }}
                renderValue={v => v
                  ? <span style={{ fontSize: '0.82rem', color: QUEUE_META[v]?.text, fontWeight: 600 }}>{QUEUE_META[v]?.label}</span>
                  : <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>All Queues</span>
                }>
                <MenuItem value=""><em style={{ fontStyle: 'normal', color: '#64748b', fontSize: '0.83rem' }}>All Queues</em></MenuItem>
                <MenuItem value="RESTAURANT"><Typography fontSize="0.83rem" color="#5b21b6" fontWeight={600}>Restaurant</Typography></MenuItem>
                <MenuItem value="ADMIN"><Typography fontSize="0.83rem" color="#075985" fontWeight={600}>Admin</Typography></MenuItem>
              </Select>
            </FormControl>
            {hasActiveFilter && (
              <Button size="small" onClick={() => { setSearch(''); setQueueFilter(''); setStatusFilter('') }}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none', color: '#64748b', fontSize: '0.78rem', borderRadius: '8px', px: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', '&:hover': { color: '#0f172a', borderColor: '#cbd5e1' } }}>
                Clear ×
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Results line */}
        {hasActiveFilter && (
          <Typography fontSize="0.76rem" color="#94a3b8" mb={1.5} px={0.5}>
            {tickets.length} result{tickets.length !== 1 ? 's' : ''} — filtered
          </Typography>
        )}

        {/* Table */}
        <Paper elevation={0} sx={{ borderRadius: '14px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <DataTable
            columns={columns}
            data={tickets}
            progressPending={loading}
            progressComponent={
              <Box sx={{ py: 6, width: '100%', textAlign: 'center', bgcolor: '#fff' }}>
                <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
              </Box>
            }
            pagination
            paginationPerPage={20}
            highlightOnHover
            pointerOnHover
            onRowClicked={openDrawer}
            customStyles={{
              table: { style: { backgroundColor: '#ffffff' } },
              headRow: { style: { backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' } },
              headCells: { style: { color: '#64748b', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', paddingTop: 13, paddingBottom: 13 } },
              rows: {
                style: { backgroundColor: '#ffffff', borderBottom: '1px solid #f1f5f9', minHeight: 58 },
                highlightOnHoverStyle: { backgroundColor: '#f8fafc', cursor: 'pointer', borderColor: '#f1f5f9', outline: 'none' },
              },
              pagination: { style: { backgroundColor: '#ffffff', color: '#64748b', borderTop: '1px solid #f1f5f9', fontSize: '0.8rem' } },
            }}
            noDataComponent={
              <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#fff', width: '100%' }}>
                <SupportAgentIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1.5 }} />
                <Typography fontWeight={600} color="#94a3b8" fontSize="0.9rem">No tickets found</Typography>
                {hasActiveFilter && <Typography fontSize="0.8rem" color="#cbd5e1" mt={0.5}>Try clearing the search or filters</Typography>}
              </Box>
            }
          />
        </Paper>
      </Container>

      <RefundBonusDialog open={bonusOpen} onClose={() => setBonusOpen(false)} />

      <TicketDrawer
        ticket={selectedTicket}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onRefresh={refetch}
      />
    </Box>
  )
}

export default SupportTickets
